import React, { useState, useEffect, useRef } from "react";
import { Link as LinkR, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Bio } from "../../data/constants";
import { MenuRounded, CloseRounded } from "../Icons";
import { navItemVariants } from "../../utils/motion";
import { useLoaderComplete } from "../../utils/useLoaderComplete";
import { lockScroll, unlockScroll } from "../../utils/smoothScroll";
import { motion } from "framer-motion";

const Nav = styled.nav`
  background-color: ${({ theme }) => theme.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 1002;
  color: white;
  font-size: 24px;
  letter-spacing: 1px;
  font-weight: 900;
`;

const NavbarContainer = styled.div`
  color: ${({ theme }) => theme.black};
  width: 90%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: calc(14px + var(--safe-top)) 0 14px;
  font-size: 1rem;
`;


const NavLogo = styled(LinkR)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 6px 0;
  font-weight: 400;
  font-size: clamp(18px, 4vw, 24px);
  text-decoration: none;
  color: ${({ theme }) => theme.text_primary};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.text_primary};
    outline-offset: 4px;
  }

  @media screen and (max-width: 768px) {
    margin: 0;
  }
`;

const NavItems = styled.ul`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 0 6px;
  margin: 0;
  list-style: none;

  @media screen and (max-width: 952px) {
    display: none;
  }
`;

const NavLink = styled(LinkR)`
  position: relative;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  text-decoration: none;
  padding: 8px 6px;
  font-weight: 400;
  font-size: 16px;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 0;
    height: 3px;
    background-color: currentColor;
    transition: width 0.3s cubic-bezier(0.645, 0.045, 0.355, 1), left 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  }

  @media (hover: hover) {
    &:hover::after {
      width: 100%;
      left: 0;
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.text_primary};
    outline-offset: 4px;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: end;
  align-items: center;
  padding: 0;
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const ButtonItem = styled(motion.div)`
  display: flex;
  align-items: center;

  @media screen and (max-width: 952px) {
    display: none;
  }
`;

const GithubButton = styled.a`
  color: ${({ theme }) => theme.text_primary};
  justify-content: center;
  display: flex;
  align-items: center;
  border-radius: 50px;
  cursor: pointer;
  padding: 10px 16px;
  font-size: 20px;
  font-weight: 400;
  transition: all 0.6s ease-in-out;
  text-decoration: none;
  margin-left: auto;

  @media (max-width: 640px) {
    border: none;
    background: none;
    font-size: 18px;
  }

  @media (max-width: 952px) {
    display: none;
  }

`;

const MobileIcon = styled.button`
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.text_primary};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.text_primary};
    outline-offset: 2px;
    border-radius: 8px;
  }

  @media screen and (max-width: 952px) {
    display: flex;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  transition: opacity 0.3s ease;
  z-index: 1000;
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(360px, 84vw);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: calc(92px + var(--safe-top)) 24px calc(24px + var(--safe-bottom));
  background: ${({ theme }) => theme.bg};
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.45);
  transform: ${({ $isOpen }) => ($isOpen ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.35s ease;
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  z-index: 1001;
  overflow-y: auto;
  overscroll-behavior: contain;

  a {
    width: 100%;
    padding: 14px 8px;
    font-size: 18px;
  }
`;

const MobileMenuFooter = styled(GithubButton)`
  display: flex;
  margin: 16px 0 0;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.text_primary};
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const loaderComplete = useLoaderComplete();
  const itemMotion = {
    variants: navItemVariants,
    initial: "hidden",
    animate: loaderComplete ? "visible" : "hidden",
  };

  // Close the menu whenever the route changes.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  // Lock background scroll while the menu is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    lockScroll();
    return () => {
      document.body.style.overflow = previous;
      unlockScroll();
    };
  }, [isOpen]);

  // Close on Escape and move focus into / out of the menu.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const first = menuRef.current?.querySelector("a, button");
    if (first) first.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <Nav>
      <NavbarContainer>
        <motion.div {...itemMotion}>
          <NavLogo to="/">PREX</NavLogo>
        </motion.div>
        <motion.div {...itemMotion}>
          <MobileIcon
            ref={toggleRef}
            type="button"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            onClick={() => setIsOpen((open) => !open)}
          >
            {isOpen ? (
              <CloseRounded style={{ color: "inherit" }} />
            ) : (
              <MenuRounded style={{ color: "inherit" }} />
            )}
          </MobileIcon>
        </motion.div>
        <NavItems>
          <motion.li {...itemMotion}>
            <NavLink to="/#Home">home</NavLink>
          </motion.li>
          <motion.li {...itemMotion}>
            <NavLink to="/#Skills">skills</NavLink>
          </motion.li>
          <motion.li {...itemMotion}>
            <NavLink to="/#Experience">experience</NavLink>
          </motion.li>
          <motion.li {...itemMotion}>
            <NavLink to="/#projects">projects</NavLink>
          </motion.li>
          <motion.li {...itemMotion}>
            <NavLink to="/#Footer">contact</NavLink>
          </motion.li>
        </NavItems>

        <Backdrop $isOpen={isOpen} onClick={closeMenu} aria-hidden="true" />

        <MobileMenu
          id="mobile-nav"
          ref={menuRef}
          $isOpen={isOpen}
          aria-hidden={!isOpen}
          data-lenis-prevent
        >
          <NavLink onClick={closeMenu} to="/#Home">home</NavLink>
          <NavLink onClick={closeMenu} to="/#Skills">skills</NavLink>
          <NavLink onClick={closeMenu} to="/#Experience">experience</NavLink>
          <NavLink onClick={closeMenu} to="/#projects">projects</NavLink>
          <NavLink onClick={closeMenu} to="/#Footer">contact</NavLink>
          <MobileMenuFooter onClick={closeMenu} href={Bio.github} target="_blank" rel="noopener noreferrer">
            GITHUB
          </MobileMenuFooter>
        </MobileMenu>

        <ButtonItem {...itemMotion}>
          <ButtonContainer>
            <GithubButton href={Bio.github} target="_blank" rel="noopener noreferrer">
              GITHUB
            </GithubButton>
          </ButtonContainer>
        </ButtonItem>
      </NavbarContainer>
    </Nav>
  );
};

export default Navbar;
